<template>
  <multiselect
    ref="multiselect"
    v-bind="{ ...$attrs, ...context.attributes }"
    :tabindex="Number(-1)"
    :class="`formulate-input-element formulate-input-element--${context.type} input-${size}`"
    :custom-label="customLabel"
    :data-type="context.type"
    :internal-search="false"
    :loading="isLoading"
    :options="options"
    :optionHeight="40"
    :maxHeight="limit * 40"
    :track-by="trackBy"
    :multiple="multiple"
    :placeholder="placeholder"
    :value="inputValueGetter()"
    :taggable="taggable"
    :tag-placeholder="taggable ? 'Add this as new tag' : ''"
    :allow-empty="allowEmpty"
    @tag="addTag"
    @input="inputValueSetter"
    @close="handleClose"
    @open="handleOpen"
    @pagination="handlePagination"
    @search-change="handleSearchChange"
  >
    <!-- Custom dropdown toggle -->
    <template #caret="{ toggle }">
      <div class="mc-caret__icon" @mousedown.prevent.stop="toggle">
        <i v-if="!isOpenDropdown" class="icon-outline_chevron-down"></i>
        <i v-else class="icon-outline_close"></i>
      </div>
    </template>

    <!-- Slot: after the list of options, when this element is in view, trigger pagination event -->
    <template #afterList>
      <div v-if="isLoading" class="px-2 py-2 d-flex gap-3 flex-column">
        <b-skeleton v-for="(i, idx) in ['100', '100']" :key="idx" :width="i" />
      </div>
      <div v-observe-visibility="handleElementObserved" style="height: 10px" />
    </template>

    <template #selection="{ isOpen }">
      <i v-if="isOpen && hasIconSearch" class="mc-fa icon-fill_search me-1"></i>
      <span v-if="isOpen && !hasIconSearch" class="me-2"></span>
    </template>

    <template
      v-if="hasImage || (selectedLabel && !multiple)"
      slot="singleLabel"
      slot-scope="options"
    >
      <!-- For select options with image -->
      <div v-if="hasImage" class="d-flex flex-row align-items-center">
        <img
          class="option__image"
          :src="options.option.img"
          :alt="options.option.value"
          :style="options.option.style"
        />
        <div class="option__desc ps-3">
          <span class="option__title">{{ options.option.label }}</span>
        </div>
      </div>
      <template v-else>
        {{ getSelectedLabel(options.option) }}
      </template>
    </template>
    <template v-if="hasImage" slot="option" slot-scope="options">
      <div class="d-flex flex-row align-items-center">
        <img
          class="option__image"
          :src="options.option.img"
          :alt="options.option.value"
          :style="options.option.style"
        />
        <div class="option__desc ps-3">
          <span class="option__title">{{ options.option.label }}</span>
        </div>
      </div>
    </template>

    <template v-if="hasDelete" slot="option" slot-scope="options">
      <div class="d-flex flex-row align-items-center">
        <div class="option__desc ps-3">
          <span class="option__title">{{ options.option.label }}</span>
          <i class="mc-fa icon-fill_trash" @click="showModalDeleteFilter(options)"></i>
        </div>
      </div>
    </template>
  </multiselect>
</template>

<script>
import Multiselect from 'vue-multiselect'
import { debounce } from '@/mixin/helper'

export default {
  components: {
    Multiselect,
  },
  props: {
    context: {
      type: Object,
      required: true,
    },
    fullObject: {
      type: [Object, Array],
      required: false,
    },
    trackBy: {
      type: String,
      default: 'id',
    },
    optionLabelFn: {
      type: Function,
      default: ({ item }) => item,
    },
    selectedLabel: {
      type: String,
      default: null,
    },
    optionsGetter: {
      type: [Function, null],
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'md',
    },
    hasImage: {
      type: Boolean,
      default: false,
    },
    hasDelete: {
      type: Boolean,
      default: false,
    },
    taggable: {
      type: Boolean,
      default: false,
    },
    tagCustom: {
      type: [Function, null],
    },
    isMandatory: {
      type: Boolean,
      default: false,
    },
    hasIconSearch: {
      type: Boolean,
      default: true,
    },
    rowItem: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  emits: ['update:fullObject', 'addTag'],
  data() {
    return {
      options: [],
      isLoading: false,
      isOpenDropdown: false,
      limit: 5,
      page: null,
      totalPage: null,
      search: '',
    }
  },
  computed: {
    placeholder() {
      if (this.taggable) {
        return this.$t('global.tagText')
      }
      if (this.isOpenDropdown) {
        return this.$t('global.search')
      }
      return this.context.attributes.placeholder
    },
    optionsContainerRef() {
      return this.$refs?.multiselect?.$refs?.list
    },
    hasNextPage() {
      // If totalCount is null The data has not been fetched yet
      // so we return true to get the actual total count from the first request
      if (this.totalCount == null) return true
      if (this.page == null) return true
      if (this.page == this.totalPage) return false
      return this.page * this.limit < this.totalCount
    },
    allowEmpty() {
      return !this.isMandatory
    },
  },
  mounted() {
    document.addEventListener('scroll', this.repositionDropDown, true)
  },
  destroyed() {
    document.removeEventListener('scroll', this.repositionDropDown, true)
  },
  methods: {
    addTag(newTag) {
      this.$emit('addTag', newTag)
    },

    async getData(args = { reset: false }) {
      if (this.isLoading) return
      if (!this.optionsGetter) return
      if (!args.reset && !this.hasNextPage) return

      this.isLoading = true

      let nextPage = this.page + 1
      if (args.reset) {
        nextPage = 1
        this.options = []
      }

      let res = await this.optionsGetter({
        limit: this.limit,
        page: nextPage,
        search: this.search,
        rowItem: this.rowItem,
        formValues: this.context.getFormValues ? this.context.getFormValues() : {},
      })

      if (this.taggable && res.data.paginated_result.length == 0) {
        res = await this.optionsGetter()
      }

      if (res.errors) {
      this.resetStickyParentZIndex()
        this.isLoading = false
      this.resetStickyParentZIndex()
        return
      }

      this.totalPage = res.metadata.total_page
      this.totalCount = res.metadata.total_count
      this.page = res.metadata.page

      this.options = args.reset
        ? res.data.paginated_result
        : [...this.options, ...res.data.paginated_result]

      await this.$nextTick()
      this.isLoading = false
    },
    customLabel(item) {
      if (!this.optionLabelFn) return item

      return this.optionLabelFn({ item, options: this.options })
    },
    getSelectedLabel(item) {
      if (item === undefined || item === null) return ''
      if (typeof item !== 'object') return String(item)

      const label = item[this.selectedLabel]
      if (label !== undefined && label !== null && label !== '') return String(label)

      return this.customLabel(item)
    },
    // Since we only track the ID, we still need to pass the whole object
    // to vue-multiselect so every selection is detected
    inputValueGetter() {
      let _value = this.multiple ? [] : ''

      const selected = this.context.model

      // create object source from fullObject
      const objectSource = this.multiple ? this.fullObject : [this.fullObject]

      if (!Array.isArray(objectSource)) return _value

      // Display the selected value based on the object source,
      // if the select input supports multiple, loop over it first
      for (let item of objectSource) {
        if (!item) continue

        if (this.multiple && selected.includes(item[this.trackBy])) {
          _value.push(item)
        }

        // If single
        else if (selected === item[this.trackBy]) {
          _value = item
        }
      }

      return _value
    },
    // Since we usually only track the ID, we need to parse the option
    // selected by vue-multiselect before setting it in the model
    inputValueSetter(input) {
      // If mandatory, prevent unselecting (empty value)
      if (this.isMandatory) {
        if (this.multiple && (!input || input.length === 0)) return
        if (!this.multiple && !input) return
      }

      const parsed = Array.isArray(input)
        ? input.map((i) => i[this.trackBy])
        : !input
        ? input
        : input[this.trackBy]

      this.context.model = parsed
      this.$emit('update:fullObject', input)
    },
    async handleOpen() {
      this.bumpStickyParentZIndex()
      this.repositionDropDown()
      await this.getData({ reset: true })
      await this.$nextTick()
      this.isOpenDropdown = true
      this.repositionDropDown()
    },

    handleClose() {
      this.resetStickyParentZIndex()
      this.isLoading = false
      this.isOpenDropdown = false
      this.context.blurHandler()
    },
    async handlePagination() {
      if (this.isLoading) return

      await this.getData()
    },
    // This function will get called when:
    // - Search value is changed
    // - Selecting an option from search (which will reset the search value)
    handleSearchChange: debounce(async function (search) {
      if (this.isLoading) return

      this.search = search
      await this.$nextTick()

      // If the dropdown is NOT open,
      // it means that user already selected an option from search
      // so we don't need to reset the options
      // since it is needed for displaying the value
      if (!this.isOpenDropdown) return

      await this.getData({ reset: true })
    }, 500),
    handleElementObserved(isVisible) {
      if (!isVisible) return

      this.handlePagination()

      // Scroll to bottom when pagination is triggered
      this.$nextTick().then(() => {
        if (typeof this.optionsContainerRef !== 'function') return
        this.optionsContainerRef.scrollTo({
          top: this.optionsContainerRef.scrollHeight,
          behavior: 'smooth',
        })
      })
    },
    // Make dropdown display fixed to avoid being cut off by parent container
    // and make them stick to parent while scrolling
    repositionDropDown() {
      if (!this.optionsContainerRef?.style) return

      const { top, height, left } = this.$el.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const dropdownHeight =
        parseFloat(window.getComputedStyle(this.optionsContainerRef).maxHeight) ||
        this.optionsContainerRef.offsetHeight ||
        this.limit * 40
      const spaceAbove = top
      const spaceBelow = viewportHeight - (top + height)
      const shouldOpenUpward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow

      this.optionsContainerRef.style.width = `${this.$el.clientWidth}px`
      this.optionsContainerRef.style.position = 'fixed'
      this.optionsContainerRef.style.zIndex = '1060'
      this.optionsContainerRef.style.left = `${left}px`

      if (shouldOpenUpward) {
        this.optionsContainerRef.style.top = 'auto'
        this.optionsContainerRef.style.bottom = `${viewportHeight - top}px`
        return
      }

      this.optionsContainerRef.style.bottom = 'auto'
      this.optionsContainerRef.style.top = `${top + height}px`
    },
    bumpStickyParentZIndex() {
      const stickyParent = this.$el.closest('td, th')
      if (!stickyParent?.style) return

      if (!stickyParent.dataset.originalZIndex) {
        stickyParent.dataset.originalZIndex = stickyParent.style.zIndex || ''
      }

      stickyParent.style.zIndex = '1061'
    },
    resetStickyParentZIndex() {
      const stickyParent = this.$el.closest('td, th')
      if (!stickyParent?.style) return

      stickyParent.style.zIndex = stickyParent.dataset.originalZIndex || ''
      delete stickyParent.dataset.originalZIndex
    },
    // Methods to be called externally is in PascalCase format
    // methods to be called by parent as side effect when other fields changes
    // e.g. Delivery Form -> customer_notes should changed when product_id changed
    ResetProperties() {
      this.options = []
      this.isLoading = false
      this.limit = 5
      this.page = null
      this.totalPage = null
      this.optionsSearch = ''
    },

    showModalDeleteFilter(options) {
      this.$emit('delete', options.option.label)
    },
  },
}
</script>
